import { useAuth0 } from "@auth0/auth0-react";
import { BellIcon } from "@heroicons/react/24/outline";
import { useQueryClient } from "@tanstack/react-query";
import { NotificationActions, ReceivedWebSocketMessage, WebSocketMessage, WebSocketServices } from "@utils/constants";
import { unwrapConnection } from "@utils/utils";
import React, { useEffect, useRef, useState } from "react";
import useWebSocket from "react-use-websocket";

import { Badge, PopoverPanel } from "@fronterahealth/frontera-ui-components";

import { NotificationsType } from "@api/graphql/types-and-hooks";
import { newNotification } from "@components/NotificationToast/NotificationToast";
import { NotificationsPanel } from "@components/NotificationsPanel/NotificationsPanel";
import { handleReviewCommentsChanged } from "@components/QaReview/hooks/reviewCommentsSignal";
import { notifyError } from "@components/notifications/notifications";
import { useBreakpoint } from "@pages/VideoSessionsV2/hooks/useMediaMatcher";
import { useNotificationsData } from "@providers/NotificationsProvider";

interface NotificationBellProps {}

export const NotificationBell: React.FC<NotificationBellProps> = ({}) => {
  const { isMobile } = useBreakpoint();
  const [isMobilePanelOpen, setIsMobilePanelOpen] = useState(false);

  // set access token
  const { getAccessTokenSilently } = useAuth0();
  const [accessToken, setAccessToken] = useState<string>();

  useEffect(() => {
    let mounted = true;

    (async () => {
      try {
        const token = await getAccessTokenSilently();
        if (mounted) {
          setAccessToken(token);
        }
      } catch (error) {
        console.error("Failed to get access token:", error);
      }
    })();

    return () => {
      mounted = false;
    };
  }, [getAccessTokenSilently]);

  const queryClient = useQueryClient();
  const [isOnline, setIsOnline] = useState<boolean>(false);
  const {
    notifications,
    totalUnread,
    readNotification,
    readAllNotifications,
    dismissNotification,
    refetchNotifications,
  } = useNotificationsData();

  // Track known notification IDs via ref to avoid stale closures in WebSocket callback
  const knownNotificationIdsRef = useRef<Set<string>>(new Set());

  useEffect(() => {
    knownNotificationIdsRef.current = new Set(notifications.map((n) => n.id));
  }, [notifications]);

  const socketUrl = accessToken ? import.meta.env.VITE_WEBSOCKET_DOMAIN : null;

  const { sendJsonMessage } = useWebSocket<ReceivedWebSocketMessage>(socketUrl, {
    share: true,
    onClose: () => {
      setIsOnline(false);
    },
    onError: (error) => {
      console.error("WebSocket error:", error);
    },
    onMessage: (event) => {
      if (!accessToken) {
        return;
      }

      const wsMessage = JSON.parse(event.data) as ReceivedWebSocketMessage;

      if (wsMessage.payload.status !== 200) {
        notifyError(wsMessage.payload.message);
        return;
      }

      // for new connections; we can't use on open because it won't run for a shared WS connection
      if (!isOnline && !wsMessage.service) {
        const subscribePayload: WebSocketMessage = {
          service: WebSocketServices.NOTIFICATION,
          action: NotificationActions.SUBSCRIBE,
          timestamp: new Date(),
          accessToken: accessToken,
          payload: {},
        };
        sendJsonMessage(subscribePayload);
        return;
      }

      // handle incoming notifications
      if (wsMessage.service === WebSocketServices.NOTIFICATION) {
        switch (wsMessage.action) {
          case NotificationActions.SUBSCRIBE:
            setIsOnline(true);
            refetchNotifications().catch((error) => {
              console.error("Failed to refetch notifications on reconnect:", error);
            });
            return;
          case NotificationActions.NEW: {
            // Team Review comments changed on a report: not a bell notification, so only the review panel reloads.
            // Acting on the payload is safe here: its report id only picks which query to refetch, and the server
            // still decides what that query returns.
            if (handleReviewCommentsChanged(wsMessage.payload.message, queryClient)) {
              return;
            }
            // Refetch from backend instead of trusting WebSocket payload directly.
            // The backend filters notifications by authenticated user, preventing
            // cross-user notification leakage when the WebSocket broadcasts to all org members.
            const knownIds = knownNotificationIdsRef.current;
            refetchNotifications()
              .then((result) => {
                const updated = unwrapConnection(result.data?.getNotifications) || [];
                const newIds: string[] = [];
                for (const notif of updated) {
                  if (!knownIds.has(notif.id)) {
                    newIds.push(notif.id);
                    newNotification(notif as NotificationsType);
                  }
                }
                // Eagerly update ref so concurrent refetches won't re-toast the same IDs
                for (const id of newIds) {
                  knownNotificationIdsRef.current.add(id);
                }
              })
              .catch((error) => {
                console.error("Failed to refetch notifications:", error);
              });
            return;
          }
        }
      }
    },
    shouldReconnect: () => !!accessToken,
  });

  const panelContent = (
    <NotificationsPanel
      notifications={notifications}
      readNotification={readNotification}
      readAllNotifications={readAllNotifications}
      dismissNotification={dismissNotification}
      totalUnread={totalUnread}
      onClose={isMobile ? () => setIsMobilePanelOpen(false) : undefined}
    />
  );

  const bellButton = (
    <div className="relative -mb-2">
      <BellIcon className="h-6 w-6 text-text-primary" />
      {totalUnread > 0 && (
        <Badge
          text={totalUnread > 10 ? "10+" : totalUnread.toString()}
          appearance="error"
          className="absolute -top-2 -right-2 h-5! px-1.5! text-xs!"
        />
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          onClick={() => setIsMobilePanelOpen(true)}
          aria-label="Notifications"
          className="focus:outline-hidden"
        >
          {bellButton}
        </button>

        {isMobilePanelOpen && <div className="fixed inset-2 z-50 flex flex-col bg-white">{panelContent}</div>}
      </>
    );
  }

  return (
    <PopoverPanel width="w-[450px]" height="h-[80vh] overflow-hidden!" direction="bottom-left" content={panelContent}>
      {bellButton}
    </PopoverPanel>
  );
};
